import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle2,
  ChevronRight,
  Loader2,
  RefreshCw,
  ShieldAlert,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { useNavigation } from '@/context/NavigationContext';
import { useSession } from '@/context/SessionContext';
import {
  type AgentAccessRequestRecord,
  getAgentAccessSummary,
  redeemAgentAccessCode,
} from '@/lib/agent-access';
import { redeemPartnerInvite } from '@/lib/partner-links';
import { redeemTenantMemberInvite } from '@/lib/tenant-users';
import {
  attachOnboardingSession,
  createOnboardingSession,
  getMyTenantApplication,
  getOnboardingSession,
  listTenantActions,
  OnboardingError,
  saveMyTenantApplication,
  submitMyTenantApplication,
  type OnboardingSessionRecord,
  type TenantApplicationRecord,
  updateOnboardingSession,
} from '@/lib/onboarding';
import { useScope } from '@workspace/scope-context';
import {
  Callout,
  cn,
  InspectorSection,
  PropertyGrid,
  PropertyItem,
  RegistryAvatar,
  RegistryList,
  RegistryRow,
  RegistryStatus,
  SectionEyebrow,
  SilkInput,
  SilkSelect,
} from '@workspace/silk-ui';

// ── Constants ─────────────────────────────────────────────────────────────────

const REFRESH_INTERVAL_MS = 15000;

const EMPLOYEE_COUNT_OPTIONS = [
  { value: '1-10', label: '1 – 10 employees' },
  { value: '11-50', label: '11 – 50 employees' },
  { value: '51-250', label: '51 – 250 employees' },
  { value: '251-1000', label: '251 – 1,000 employees' },
  { value: '1000+', label: '1,000+ employees' },
];

const COMPANY_TYPE_OPTIONS = [
  { value: 'telecom_reseller', label: 'Telecom reseller / agency' },
  { value: 'telecom_operator', label: 'Telecom operator / carrier' },
  { value: 'systems_integrator', label: 'Systems integrator' },
  { value: 'enterprise_buyer', label: 'Enterprise buyer / internal team' },
  { value: 'other', label: 'Other' },
];

const COUNTRY_OPTIONS = [
  { value: 'US', label: 'United States' },
  { value: 'GB', label: 'United Kingdom' },
  { value: 'CA', label: 'Canada' },
  { value: 'AU', label: 'Australia' },
  { value: 'NZ', label: 'New Zealand' },
  { value: 'IE', label: 'Ireland' },
  { value: 'DE', label: 'Germany' },
  { value: 'FR', label: 'France' },
  { value: 'NL', label: 'Netherlands' },
  { value: 'SE', label: 'Sweden' },
  { value: 'NO', label: 'Norway' },
  { value: 'DK', label: 'Denmark' },
  { value: 'FI', label: 'Finland' },
  { value: 'CH', label: 'Switzerland' },
  { value: 'AT', label: 'Austria' },
  { value: 'BE', label: 'Belgium' },
  { value: 'ES', label: 'Spain' },
  { value: 'IT', label: 'Italy' },
  { value: 'PT', label: 'Portugal' },
  { value: 'PL', label: 'Poland' },
  { value: 'CZ', label: 'Czechia' },
  { value: 'HU', label: 'Hungary' },
  { value: 'RO', label: 'Romania' },
  { value: 'JP', label: 'Japan' },
  { value: 'KR', label: 'South Korea' },
  { value: 'SG', label: 'Singapore' },
  { value: 'HK', label: 'Hong Kong' },
  { value: 'IN', label: 'India' },
  { value: 'MY', label: 'Malaysia' },
  { value: 'TH', label: 'Thailand' },
  { value: 'ID', label: 'Indonesia' },
  { value: 'PH', label: 'Philippines' },
  { value: 'AE', label: 'United Arab Emirates' },
  { value: 'SA', label: 'Saudi Arabia' },
  { value: 'QA', label: 'Qatar' },
  { value: 'IL', label: 'Israel' },
  { value: 'ZA', label: 'South Africa' },
  { value: 'NG', label: 'Nigeria' },
  { value: 'KE', label: 'Kenya' },
  { value: 'EG', label: 'Egypt' },
  { value: 'BR', label: 'Brazil' },
  { value: 'MX', label: 'Mexico' },
  { value: 'AR', label: 'Argentina' },
  { value: 'CL', label: 'Chile' },
  { value: 'CO', label: 'Colombia' },
];

type ApplicationWizardStep = 'info' | 'details' | 'review';

const APPLICATION_STEPS: Array<{ id: ApplicationWizardStep; label: string; shortLabel: string }> = [
  { id: 'info', label: 'Your company', shortLabel: 'Company' },
  { id: 'details', label: 'Your details', shortLabel: 'Details' },
  { id: 'review', label: 'Review & submit', shortLabel: 'Review' },
];

// ── Types ─────────────────────────────────────────────────────────────────────

interface ProvisioningGateProps {
  children: ReactNode;
}

interface TenantApplicationFormState {
  companyName: string;
  website: string;
  employeeCountBand: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  countryCode: string;
  companyType: string;
  intendedUse: string;
  notes: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function hasRequiredOnboarding(actions: Awaited<ReturnType<typeof listTenantActions>>) {
  return actions.some(
    action => action.required && !['completed', 'dismissed', 'expired'].includes(action.status),
  );
}

function formatPendingRequestLabel(request: AgentAccessRequestRecord) {
  return request.tenantName || request.tenantSlug || request.tenantId;
}

function buildPathWithoutQueryParam(path: string, key: string) {
  const [pathname, search = ''] = path.split('?');
  const params = new URLSearchParams(search);
  params.delete(key);
  const nextQuery = params.toString();
  return nextQuery ? `${pathname}?${nextQuery}` : pathname;
}

function buildPathWithQueryParam(path: string, key: string, value: string) {
  const [pathname, search = ''] = path.split('?');
  const params = new URLSearchParams(search);
  params.set(key, value);
  const nextQuery = params.toString();
  return nextQuery ? `${pathname}?${nextQuery}` : pathname;
}

function createEmptyApplicationDraft(email: string): TenantApplicationFormState {
  return {
    companyName: '',
    website: '',
    employeeCountBand: '',
    contactName: '',
    contactEmail: email,
    contactPhone: '',
    countryCode: 'US',
    companyType: '',
    intendedUse: '',
    notes: '',
  };
}

function mapApplicationToForm(
  application: TenantApplicationRecord | null,
  fallbackEmail: string,
): TenantApplicationFormState {
  if (!application) return createEmptyApplicationDraft(fallbackEmail);
  return {
    companyName: application.companyName ?? '',
    website: application.website ?? '',
    employeeCountBand: application.employeeCountBand ?? '',
    contactName: application.contactName ?? '',
    contactEmail: application.contactEmail ?? fallbackEmail,
    contactPhone: application.contactPhone ?? '',
    countryCode: application.countryCode ?? 'US',
    companyType: application.companyType ?? '',
    intendedUse: application.intendedUse ?? '',
    notes: application.notes ?? '',
  };
}

function mapSessionDraftToForm(
  current: TenantApplicationFormState,
  onboardingSession: OnboardingSessionRecord | null,
) {
  const draft = onboardingSession?.applicationDraft ?? {};
  return {
    companyName:
      typeof draft.companyName === 'string' && draft.companyName
        ? draft.companyName
        : current.companyName,
    website: typeof draft.website === 'string' ? draft.website : current.website,
    employeeCountBand:
      typeof draft.employeeCountBand === 'string' && draft.employeeCountBand
        ? draft.employeeCountBand
        : current.employeeCountBand,
    contactName:
      typeof draft.contactName === 'string' && draft.contactName
        ? draft.contactName
        : current.contactName,
    contactEmail:
      typeof draft.contactEmail === 'string' && draft.contactEmail
        ? draft.contactEmail
        : current.contactEmail,
    contactPhone:
      typeof draft.contactPhone === 'string' ? draft.contactPhone : current.contactPhone,
    countryCode:
      typeof draft.countryCode === 'string' && draft.countryCode
        ? draft.countryCode
        : current.countryCode,
    companyType:
      typeof draft.companyType === 'string' && draft.companyType
        ? draft.companyType
        : current.companyType,
    intendedUse: typeof draft.intendedUse === 'string' ? draft.intendedUse : current.intendedUse,
    notes: typeof draft.notes === 'string' ? draft.notes : current.notes,
  };
}

function buildTenantApplicationPayload(form: TenantApplicationFormState) {
  return {
    companyName: form.companyName.trim(),
    website: form.website.trim() || null,
    employeeCountBand: form.employeeCountBand,
    contactName: form.contactName.trim(),
    contactEmail: form.contactEmail.trim(),
    contactPhone: form.contactPhone.trim() || null,
    countryCode: form.countryCode.trim().toUpperCase(),
    companyType: form.companyType,
    intendedUse: form.intendedUse.trim() || null,
    notes: form.notes.trim() || null,
    metadata: null,
  };
}

function validateApplicationStep(
  step: string,
  form: TenantApplicationFormState,
): Record<string, string> {
  const errors: Record<string, string> = {};
  if (step === 'info') {
    if (!form.companyName.trim()) errors.companyName = 'Company name is required.';
    if (!form.companyType) errors.companyType = 'Select a company type.';
    if (!form.employeeCountBand) errors.employeeCountBand = 'Select a team size.';
  }
  if (step === 'details') {
    if (!form.contactName.trim()) errors.contactName = 'Contact name is required.';
    if (!form.contactEmail.trim()) {
      errors.contactEmail = 'Contact email is required.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.contactEmail.trim())) {
      errors.contactEmail = 'Enter a valid email address.';
    }
  }
  return errors;
}

function validateAllApplicationFields(form: TenantApplicationFormState): Record<string, string> {
  return {
    ...validateApplicationStep('info', form),
    ...validateApplicationStep('details', form),
  };
}

// ── Small UI helpers ──────────────────────────────────────────────────────────

function FieldError({ error }: { error?: string }) {
  if (!error) return null;
  return (
    <p className="flex items-center gap-1 text-xs text-destructive">
      <AlertCircle className="h-3 w-3 shrink-0" />
      {error}
    </p>
  );
}

function ApplicationStepIndicator({
  steps,
  activeStep,
}: {
  steps: Array<{ id: string; shortLabel: string }>;
  activeStep: string;
}) {
  const activeIndex = steps.findIndex(s => s.id === activeStep);
  return (
    <div className="flex items-start justify-center">
      {steps.map((step, index) => {
        const isDone = index < activeIndex;
        const isActive = index === activeIndex;
        return (
          <div key={step.id} className="flex items-start">
            <div className="flex flex-col items-center gap-2">
              <div
                className={cn(
                  'flex h-8 w-8 items-center justify-center rounded-full text-xs font-black transition-all duration-200',
                  isDone && 'bg-primary text-primary-foreground',
                  isActive && 'bg-primary text-primary-foreground ring-4 ring-primary/20',
                  !isDone && !isActive && 'border-2 border-border/50 bg-background text-muted-foreground/60',
                )}
              >
                {isDone ? <CheckCircle2 className="h-3.5 w-3.5" /> : index + 1}
              </div>
              <span
                className={cn(
                  'text-[11px] font-bold transition-colors',
                  isActive ? 'text-foreground' : 'text-muted-foreground/40',
                )}
              >
                {step.shortLabel}
              </span>
            </div>
            {index < steps.length - 1 && (
              <div
                className={cn(
                  'mx-3 mt-4 h-px min-w-[48px] w-20 transition-colors duration-200',
                  isDone ? 'bg-primary' : 'bg-border/40',
                )}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function ProvisioningGate({ children }: ProvisioningGateProps) {
  const navigation = useNavigation();
  const { session, refresh: refreshSession } = useSession();
  const { activeTenant, refresh: refreshScope } = useScope();
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [checkingOnboarding, setCheckingOnboarding] = useState(false);
  const onboardingReadyTenantIdRef = useRef<string | null>(null);
  const [agentRequests, setAgentRequests] = useState<AgentAccessRequestRecord[]>([]);
  const [agentAccessLoading, setAgentAccessLoading] = useState(false);
  const [agentAccessError, setAgentAccessError] = useState<string | null>(null);
  const [redeemCode, setRedeemCode] = useState('');
  const [redeeming, setRedeeming] = useState(false);
  const [redeemError, setRedeemError] = useState<string | null>(null);
  const [application, setApplication] = useState<TenantApplicationRecord | null>(null);
  const [applicationForm, setApplicationForm] = useState<TenantApplicationFormState>(() =>
    createEmptyApplicationDraft(''),
  );
  const [applicationLoading, setApplicationLoading] = useState(false);
  const [applicationSaving, setApplicationSaving] = useState(false);
  const [applicationSubmitting, setApplicationSubmitting] = useState(false);
  const [applicationError, setApplicationError] = useState<string | null>(null);
  const [applicationNotice, setApplicationNotice] = useState<string | null>(null);
  const [onboardingSessionRecord, setOnboardingSessionRecord] =
    useState<OnboardingSessionRecord | null>(null);
  const [applicationWizardStep, setApplicationWizardStep] =
    useState<ApplicationWizardStep>('info');
  const [applicationFieldErrors, setApplicationFieldErrors] = useState<Record<string, string>>({});
  const [memberInviteRedeeming, setMemberInviteRedeeming] = useState(false);
  const [memberInviteError, setMemberInviteError] = useState<string | null>(null);
  const [partnerInviteRedeeming, setPartnerInviteRedeeming] = useState(false);
  const [partnerInviteError, setPartnerInviteError] = useState<string | null>(null);
  const [autoSaveStatus, setAutoSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const autoSaveTimerRef = useRef<number | null>(null);
  const applicationFormRef = useRef(applicationForm);
  const attemptedInviteCodeRef = useRef<string | null>(null);
  const attemptedMemberInviteRef = useRef<string | null>(null);
  const attemptedPartnerInviteRef = useRef<string | null>(null);
  const preparedPartnerInviteRef = useRef<string | null>(null);
  const portalAccess = session?.portalAccess;
  const status = portalAccess?.status ?? 'provisioning';
  const normalizedPath = useMemo(
    () => navigation.path.split('?')[0]?.replace(/\/+$/, '') || '/',
    [navigation.path],
  );
  const isSetupRoute = normalizedPath === '/setup';
  const onboardingSessionIdFromUrl = useMemo(() => {
    const search = navigation.path.includes('?')
      ? navigation.path.slice(navigation.path.indexOf('?') + 1)
      : '';
    return new URLSearchParams(search).get('onboarding_session')?.trim() ?? '';
  }, [navigation.path]);
  const inviteCodeFromUrl = useMemo(() => {
    const search = navigation.path.includes('?')
      ? navigation.path.slice(navigation.path.indexOf('?') + 1)
      : '';
    return new URLSearchParams(search).get('invite')?.trim() ?? '';
  }, [navigation.path]);
  const memberInviteTokenFromUrl = useMemo(() => {
    const search = navigation.path.includes('?')
      ? navigation.path.slice(navigation.path.indexOf('?') + 1)
      : '';
    return new URLSearchParams(search).get('member_invite')?.trim() ?? '';
  }, [navigation.path]);
  const partnerInviteTokenFromUrl = useMemo(() => {
    const search = navigation.path.includes('?')
      ? navigation.path.slice(navigation.path.indexOf('?') + 1)
      : '';
    return new URLSearchParams(search).get('partner_invite')?.trim() ?? '';
  }, [navigation.path]);

  const emailLabel = session?.email ?? 'your email address';
  const awaitingVerification = status === 'awaiting_email_verification';
  const awaitingTenantApplication = status === 'awaiting_tenant_application';
  const pendingTenantApproval = status === 'pending_tenant_approval';
  const awaitingTenantAccess = status === 'awaiting_tenant_access';
  const pendingTenantAccessApproval = status === 'pending_tenant_approval_for_access';
  const blocked = status === 'blocked';
  const ready = status === 'ready';
  const needsAgentAccess = awaitingTenantAccess || pendingTenantAccessApproval;
  const needsTenantApplication = awaitingTenantApplication || pendingTenantApproval;
  const shouldAutoRefreshStatus =
    pendingTenantApproval ||
    awaitingTenantAccess ||
    pendingTenantAccessApproval ||
    blocked;
  const attachedOnboardingSessionRef = useRef<string | null>(null);
  const rejectedApplication = application?.status === 'rejected';

  const loadAgentAccessSummary = useCallback(async () => {
    if (!needsAgentAccess) {
      setAgentRequests([]);
      setAgentAccessError(null);
      return;
    }
    setAgentAccessLoading(true);
    try {
      const summary = await getAgentAccessSummary();
      setAgentRequests(summary.requests ?? []);
      setAgentAccessError(null);
    } catch (error) {
      setAgentAccessError(
        error instanceof Error ? error.message : 'Failed to load agent access requests.',
      );
    } finally {
      setAgentAccessLoading(false);
    }
  }, [needsAgentAccess]);

  const loadTenantApplication = useCallback(async () => {
    if (!needsTenantApplication) {
      setApplication(null);
      setApplicationError(null);
      setApplicationNotice(null);
      setApplicationForm(createEmptyApplicationDraft(emailLabel));
      return;
    }
    setApplicationLoading(true);
    try {
      const nextApplication = await getMyTenantApplication();
      setApplication(nextApplication);
      setApplicationForm(current => {
        if (!nextApplication && onboardingSessionIdFromUrl) return current;
        return mapApplicationToForm(nextApplication, emailLabel);
      });
      setApplicationError(null);
    } catch (error) {
      if (error instanceof OnboardingError && error.status === 401) {
        setApplication(null);
        setApplicationError(null);
        await refreshSession();
        return;
      }
      setApplication(null);
      setApplicationError(
        error instanceof Error ? error.message : 'Failed to load your company application.',
      );
      if (!onboardingSessionIdFromUrl) {
        setApplicationForm(createEmptyApplicationDraft(emailLabel));
      }
    } finally {
      setApplicationLoading(false);
    }
  }, [emailLabel, needsTenantApplication, onboardingSessionIdFromUrl]);

  const handleRefreshStatus = useCallback(async () => {
    setCheckingStatus(true);
    try {
      await Promise.all([refreshSession(), refreshScope()]);
    } finally {
      setCheckingStatus(false);
    }
  }, [refreshScope, refreshSession]);

  const clearInviteCodeFromUrl = useCallback(() => {
    const nextPath = buildPathWithoutQueryParam(navigation.path, 'invite');
    if (nextPath !== navigation.path) navigation.navigate(nextPath, { replace: true });
  }, [navigation]);

  const clearMemberInviteFromUrl = useCallback(() => {
    const nextPath = buildPathWithoutQueryParam(navigation.path, 'member_invite');
    if (nextPath !== navigation.path) navigation.navigate(nextPath, { replace: true });
  }, [navigation]);
  const clearPartnerInviteFromUrl = useCallback(() => {
    const nextPath = buildPathWithoutQueryParam(navigation.path, 'partner_invite');
    if (nextPath !== navigation.path) navigation.navigate(nextPath, { replace: true });
  }, [navigation]);
  const effectivePartnerInviteToken = useMemo(() => {
    if (partnerInviteTokenFromUrl) {
      return partnerInviteTokenFromUrl;
    }
    if (onboardingSessionRecord?.intent === 'accept_partner_invite') {
      return onboardingSessionRecord.inviteToken?.trim() ?? '';
    }
    return '';
  }, [onboardingSessionRecord, partnerInviteTokenFromUrl]);

  const handleRedeem = useCallback(
    async (code: string, clearUrlAfterwards = false) => {
      const normalizedCode = code.trim();
      if (!normalizedCode) {
        setRedeemError('Enter an invite code to continue.');
        return;
      }
      setRedeeming(true);
      setRedeemError(null);
      try {
        await redeemAgentAccessCode(normalizedCode);
        setRedeemCode('');
        if (clearUrlAfterwards) clearInviteCodeFromUrl();
        await Promise.all([refreshSession(), refreshScope()]);
      } catch (error) {
        setRedeemError(
          error instanceof Error ? error.message : 'Failed to redeem the invite code.',
        );
        if (clearUrlAfterwards) clearInviteCodeFromUrl();
      } finally {
        setRedeeming(false);
      }
    },
    [clearInviteCodeFromUrl, refreshScope, refreshSession],
  );

  const triggerAutoSave = useCallback(() => {
    if (autoSaveTimerRef.current) window.clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = window.setTimeout(() => {
      setAutoSaveStatus('saving');
      void saveMyTenantApplication(buildTenantApplicationPayload(applicationFormRef.current))
        .then(saved => {
          setApplication(saved);
          setAutoSaveStatus('saved');
          autoSaveTimerRef.current = window.setTimeout(
            () => setAutoSaveStatus(s => (s === 'saved' ? 'idle' : s)),
            3000,
          );
        })
        .catch(() => setAutoSaveStatus('error'));
    }, 1500);
  }, []);

  const handleApplicationFieldChange = useCallback(
    (field: keyof TenantApplicationFormState, value: string) => {
      setApplicationNotice(null);
      setApplicationError(null);
      setApplicationFieldErrors(prev => {
        if (!prev[field]) return prev;
        const next = { ...prev };
        delete next[field];
        return next;
      });
      setApplicationForm(current => ({ ...current, [field]: value }));
      triggerAutoSave();
    },
    [triggerAutoSave],
  );

  const handleSaveApplication = useCallback(async () => {
    setApplicationNotice(null);
    const allErrors = validateAllApplicationFields(applicationForm);
    if (Object.keys(allErrors).length > 0) {
      setApplicationFieldErrors(allErrors);
      setApplicationError('Complete the required fields to save your draft.');
      setApplicationWizardStep('info');
      return;
    }
    setApplicationSaving(true);
    try {
      const saved = await saveMyTenantApplication(buildTenantApplicationPayload(applicationForm));
      setApplication(saved);
      setApplicationForm(mapApplicationToForm(saved, emailLabel));
      setApplicationError(null);
      setApplicationNotice('Draft saved. You can leave and come back to finish later.');
    } catch (error) {
      setApplicationError(
        error instanceof Error ? error.message : 'Failed to save your company application.',
      );
    } finally {
      setApplicationSaving(false);
    }
  }, [applicationForm, emailLabel]);

  const handleSubmitApplication = useCallback(async () => {
    setApplicationNotice(null);
    const allErrors = validateAllApplicationFields(applicationForm);
    if (Object.keys(allErrors).length > 0) {
      setApplicationFieldErrors(allErrors);
      setApplicationError('Complete all required fields before submitting.');
      setApplicationWizardStep('info');
      return;
    }
    setApplicationSubmitting(true);
    try {
      const saved = await saveMyTenantApplication(buildTenantApplicationPayload(applicationForm));
      const submitted = await submitMyTenantApplication();
      setApplication(submitted);
      setApplicationForm(mapApplicationToForm(submitted, emailLabel));
      setApplicationError(null);
      setApplicationNotice(`Submitted ${saved.companyName} for review.`);
      await Promise.all([refreshSession(), refreshScope()]);
    } catch (error) {
      setApplicationError(
        error instanceof Error ? error.message : 'Failed to submit your company application.',
      );
    } finally {
      setApplicationSubmitting(false);
    }
  }, [applicationForm, emailLabel, refreshScope, refreshSession]);

  const handleApplicationTabChange = useCallback(
    (id: string) => {
      const currentIndex = APPLICATION_STEPS.findIndex(s => s.id === applicationWizardStep);
      const targetIndex = APPLICATION_STEPS.findIndex(s => s.id === id);
      if (targetIndex > currentIndex) {
        const errors = validateApplicationStep(applicationWizardStep, applicationForm);
        if (Object.keys(errors).length > 0) {
          setApplicationFieldErrors(errors);
          return;
        }
      }
      setApplicationFieldErrors({});
      setApplicationError(null);
      setApplicationNotice(null);
      setApplicationWizardStep(id as ApplicationWizardStep);
    },
    [applicationForm, applicationWizardStep],
  );

  useEffect(() => {
    void loadAgentAccessSummary();
  }, [loadAgentAccessSummary]);

  useEffect(() => {
    void loadTenantApplication();
  }, [loadTenantApplication]);

  useEffect(() => {
    applicationFormRef.current = applicationForm;
  }, [applicationForm]);

  useEffect(() => {
    if (!onboardingSessionIdFromUrl || !session?.id) {
      setOnboardingSessionRecord(null);
      return;
    }
    let cancelled = false;
    void getOnboardingSession(onboardingSessionIdFromUrl)
      .then(async existingSession => {
        if (cancelled || !existingSession) return;
        setOnboardingSessionRecord(existingSession);
        if (attachedOnboardingSessionRef.current === onboardingSessionIdFromUrl) {
          return;
        }
        attachedOnboardingSessionRef.current = onboardingSessionIdFromUrl;
        const attachedSession = await attachOnboardingSession(onboardingSessionIdFromUrl);
        if (cancelled) return;
        setOnboardingSessionRecord(attachedSession);
        if (needsTenantApplication) {
          setApplicationForm(current => mapSessionDraftToForm(current, existingSession));
          setApplicationNotice('Resumed your saved company draft from the pre-registration flow.');
        }
      })
      .catch(error => {
        if (!cancelled) {
          setOnboardingSessionRecord(null);
          setApplicationError(
            error instanceof Error ? error.message : 'Failed to resume your onboarding draft.',
          );
        }
      });
    return () => {
      cancelled = true;
    };
  }, [needsTenantApplication, onboardingSessionIdFromUrl, session?.id]);

  useEffect(() => {
    if (ready || !shouldAutoRefreshStatus) return undefined;
    const intervalId = window.setInterval(() => {
      void handleRefreshStatus();
    }, REFRESH_INTERVAL_MS);
    return () => window.clearInterval(intervalId);
  }, [handleRefreshStatus, ready, shouldAutoRefreshStatus]);

  useEffect(() => {
    if (!needsAgentAccess || !inviteCodeFromUrl || redeeming) return;
    if (attemptedInviteCodeRef.current === inviteCodeFromUrl) return;
    attemptedInviteCodeRef.current = inviteCodeFromUrl;
    void handleRedeem(inviteCodeFromUrl, true);
  }, [handleRedeem, inviteCodeFromUrl, needsAgentAccess, redeeming]);

  useEffect(() => {
    if (!session?.id || !memberInviteTokenFromUrl) return;
    if (attemptedMemberInviteRef.current === memberInviteTokenFromUrl) return;
    attemptedMemberInviteRef.current = memberInviteTokenFromUrl;
    let cancelled = false;
    setMemberInviteRedeeming(true);
    setMemberInviteError(null);
    void redeemTenantMemberInvite(memberInviteTokenFromUrl)
      .then(async () => {
        if (cancelled) return;
        clearMemberInviteFromUrl();
        navigation.navigate('/', { replace: true });
        if (cancelled) return;
        await Promise.all([refreshSession(), refreshScope()]);
        if (cancelled) return;
      })
      .catch(error => {
        if (!cancelled)
          setMemberInviteError(
            error instanceof Error ? error.message : 'Failed to accept the tenant invite.',
          );
      })
      .finally(() => {
        if (!cancelled) setMemberInviteRedeeming(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    clearMemberInviteFromUrl,
    memberInviteTokenFromUrl,
    navigation,
    refreshScope,
    refreshSession,
    session?.id,
  ]);

  useEffect(() => {
    if (!session?.id || activeTenant?.id || !partnerInviteTokenFromUrl) return;
    if (preparedPartnerInviteRef.current === `${onboardingSessionIdFromUrl}:${partnerInviteTokenFromUrl}`) return;
    preparedPartnerInviteRef.current = `${onboardingSessionIdFromUrl}:${partnerInviteTokenFromUrl}`;
    let cancelled = false;
    setPartnerInviteRedeeming(true);
    setPartnerInviteError(null);
    void (async () => {
      try {
        const sessionId = onboardingSessionIdFromUrl
          ? (
              await updateOnboardingSession(onboardingSessionIdFromUrl, {
                intent: 'accept_partner_invite',
                inviteToken: partnerInviteTokenFromUrl,
              })
            ).id
          : (
              await createOnboardingSession({
                status: 'started',
                intent: 'accept_partner_invite',
                inviteToken: partnerInviteTokenFromUrl,
                metadata: null,
              })
            ).id;
        if (cancelled) return;
        setOnboardingSessionRecord(current => ({
          ...(current ?? {
            id: sessionId,
            status: 'started',
            intent: 'accept_partner_invite',
            createdAt: new Date().toISOString(),
          }),
          id: sessionId,
          intent: 'accept_partner_invite',
          inviteToken: partnerInviteTokenFromUrl,
        }));
        const nextPath = buildPathWithQueryParam('/', 'onboarding_session', sessionId);
        navigation.navigate(nextPath, { replace: true });
      } catch (error) {
        if (!cancelled) {
          setPartnerInviteError(
            error instanceof Error
              ? error.message
              : 'Failed to prepare the seller visibility invite for onboarding.',
          );
        }
      } finally {
        if (!cancelled) setPartnerInviteRedeeming(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    activeTenant?.id,
    navigation,
    onboardingSessionIdFromUrl,
    partnerInviteTokenFromUrl,
    session?.id,
  ]);

  useEffect(() => {
    if (!session?.id || !activeTenant?.id || !effectivePartnerInviteToken) return;
    if (attemptedPartnerInviteRef.current === `${activeTenant.id}:${effectivePartnerInviteToken}`) return;
    attemptedPartnerInviteRef.current = `${activeTenant.id}:${effectivePartnerInviteToken}`;
    let cancelled = false;
    setPartnerInviteRedeeming(true);
    setPartnerInviteError(null);
    void redeemPartnerInvite(effectivePartnerInviteToken, activeTenant.id)
      .then(async () => {
        if (cancelled) return;
        navigation.navigate('/reseller/linked-sellers', { replace: true });
        if (cancelled) return;
        await Promise.all([refreshSession(), refreshScope()]);
      })
      .catch(error => {
        if (!cancelled)
          setPartnerInviteError(
            error instanceof Error ? error.message : 'Failed to accept the seller visibility invite.',
          );
      })
      .finally(() => {
        if (!cancelled) setPartnerInviteRedeeming(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    activeTenant?.id,
    effectivePartnerInviteToken,
    navigation,
    refreshScope,
    refreshSession,
    session?.id,
  ]);

  useEffect(() => {
    if (!ready || !activeTenant?.id) {
      onboardingReadyTenantIdRef.current = null;
      setCheckingOnboarding(false);
      return;
    }
    if (isSetupRoute) {
      setCheckingOnboarding(false);
      return;
    }
    const shouldBlockWorkspace = onboardingReadyTenantIdRef.current !== activeTenant.id;
    let cancelled = false;
    if (shouldBlockWorkspace) setCheckingOnboarding(true);
    void listTenantActions(activeTenant.id)
      .then(actions => {
        if (cancelled) return;
        if (hasRequiredOnboarding(actions)) {
          onboardingReadyTenantIdRef.current = null;
          navigation.navigate('/setup', { replace: true });
          return;
        }
        onboardingReadyTenantIdRef.current = activeTenant.id;
      })
      .catch(() => {
        if (!cancelled) setCheckingOnboarding(false);
      })
      .finally(() => {
        if (!cancelled && shouldBlockWorkspace) setCheckingOnboarding(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activeTenant?.id, isSetupRoute, navigation.navigate, normalizedPath, ready]);

  // ── Derived: application wizard summary ────────────────────────────────────

  const applicationSummaryItems = useMemo(
    () => [
      {
        label: 'Company',
        value: applicationForm.companyName.trim() || '—',
        complete: !!applicationForm.companyName.trim(),
      },
      {
        label: 'Type',
        value:
          COMPANY_TYPE_OPTIONS.find(o => o.value === applicationForm.companyType)?.label ?? '—',
        complete: !!applicationForm.companyType,
      },
      {
        label: 'Team size',
        value:
          EMPLOYEE_COUNT_OPTIONS.find(o => o.value === applicationForm.employeeCountBand)?.label ??
          '—',
        complete: !!applicationForm.employeeCountBand,
      },
      {
        label: 'Contact',
        value: applicationForm.contactName.trim() || '—',
        complete: !!applicationForm.contactName.trim(),
      },
      {
        label: 'Email',
        value: applicationForm.contactEmail.trim() || '—',
        complete:
          !!applicationForm.contactEmail.trim() &&
          /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(applicationForm.contactEmail.trim()),
      },
    ],
    [applicationForm],
  );

  const applicationAllComplete = applicationSummaryItems.every(i => i.complete);

  // ── Shared field-error class helper ───────────────────────────────────────

  const appInputCls = (field: string) =>
    cn('h-9 text-sm', applicationFieldErrors[field] && 'border-destructive ring-1 ring-destructive/30');

  // ── Render: member invite redemption ───────────────────────────────────────

  if (memberInviteTokenFromUrl) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-6 py-16 text-foreground">
        <div className="w-full max-w-lg space-y-6">
          <div className="space-y-1">
            <SectionEyebrow>Tenant invite</SectionEyebrow>
            <h1 className="text-2xl font-bold text-foreground">
              {memberInviteError
                ? 'Could not accept this invite'
                : 'Connecting you to the workspace…'}
            </h1>
          </div>

          {memberInviteError ? (
            <Callout variant="error">{memberInviteError}</Callout>
          ) : memberInviteRedeeming ? (
            <Callout variant="info">
              <span className="inline-flex items-center gap-2">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Accepting the invite and refreshing your portal access…
              </span>
            </Callout>
          ) : null}

          <div className="flex flex-wrap gap-3">
            <Button onClick={() => void handleRefreshStatus()} disabled={checkingStatus}>
              <RefreshCw className={cn('mr-1.5 h-3.5 w-3.5', checkingStatus && 'animate-spin')} />
              {checkingStatus ? 'Checking…' : 'Refresh status'}
            </Button>
            <Button variant="outline" onClick={clearMemberInviteFromUrl}>
              Continue without invite
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (effectivePartnerInviteToken && activeTenant?.id) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-6 py-16 text-foreground">
        <div className="w-full max-w-lg space-y-6">
          <div className="space-y-1">
            <SectionEyebrow>Seller visibility invite</SectionEyebrow>
            <h1 className="text-2xl font-bold text-foreground">
              {partnerInviteError
                ? 'Could not accept this invite'
                : 'Connecting seller visibility…'}
            </h1>
          </div>

          {partnerInviteError ? (
            <Callout variant="error">{partnerInviteError}</Callout>
          ) : partnerInviteRedeeming ? (
            <Callout variant="info">
              <span className="inline-flex items-center gap-2">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Accepting the invite and refreshing your seller directory…
              </span>
            </Callout>
          ) : null}

          <div className="flex flex-wrap gap-3">
            <Button onClick={() => void handleRefreshStatus()} disabled={checkingStatus}>
              <RefreshCw className={cn('mr-1.5 h-3.5 w-3.5', checkingStatus && 'animate-spin')} />
              {checkingStatus ? 'Checking…' : 'Refresh status'}
            </Button>
            <Button variant="outline" onClick={clearPartnerInviteFromUrl}>
              Continue without invite
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ── Render: ready (pass-through) ──────────────────────────────────────────

  if (ready) {
    if (checkingOnboarding && !isSetupRoute) {
      return (
        <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-background text-muted-foreground">
          <Loader2 className="h-6 w-6 animate-spin" aria-hidden />
          <p className="text-sm font-medium">Preparing your workspace…</p>
        </div>
      );
    }
    return <>{children}</>;
  }

  // ── Render: company application ───────────────────────────────────────────

  if (awaitingTenantApplication) {
    return (
      <div className="min-h-screen bg-background">
        {/* Top bar */}
        <div className="border-b border-border/40 px-6 py-3.5">
          <div className="mx-auto max-w-5xl">
            <span className="text-sm font-black tracking-tight text-foreground">
              {portalAccess?.title ?? 'Company application'}
            </span>
          </div>
        </div>

        {/* Page content */}
        <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6 sm:py-14">
          {/* Heading */}
          <div className="mb-10">
            <SectionEyebrow>Tenant application</SectionEyebrow>
            <h1 className="mt-2 text-2xl font-black tracking-tight sm:text-3xl">
              {rejectedApplication ? 'Update your application' : 'Create your company to continue'}
            </h1>
            <p className="mt-2 max-w-xl text-sm text-muted-foreground">
              {rejectedApplication
                ? 'Your previous application needs changes before it can be resubmitted.'
                : 'Your account is verified. Tell us about your company so we can review and provision your tenant workspace.'}
            </p>
          </div>

          {/* Step indicator */}
          <ApplicationStepIndicator steps={APPLICATION_STEPS} activeStep={applicationWizardStep} />

          {/* Two-column body */}
          <div className="mt-10 grid items-start gap-10 lg:grid-cols-[1fr_280px]">
            {/* Left: form */}
            <div>
              {/* ── Step 1: info ──────────────────────────────────────── */}
              {applicationWizardStep === 'info' && (
                <div className="space-y-7">
                  <div className="space-y-1">
                    <p className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/60">
                      Step 1 of 3
                    </p>
                    <h2 className="text-lg font-black">Your company</h2>
                    <p className="text-sm text-muted-foreground">
                      Tell us about your organisation. Fields marked * are required.
                    </p>
                  </div>

                  {applicationError && <Callout variant="error">{applicationError}</Callout>}
                  {rejectedApplication && (
                    <Callout variant="warning">
                      <strong className="font-semibold">Application returned:</strong>{' '}
                      {application?.latestReviewReason?.trim() ||
                        'Update your details and resubmit when ready.'}
                    </Callout>
                  )}
                  {applicationLoading && (
                    <Callout variant="info">
                      <span className="inline-flex items-center gap-2">
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        Loading your saved application…
                      </span>
                    </Callout>
                  )}

                  <div className="space-y-5">
                    <div className="space-y-1.5">
                      <label htmlFor="app-company-name" className="text-xs font-medium text-foreground">
                        Company name <span className="text-destructive">*</span>
                      </label>
                      <SilkInput
                        id="app-company-name"
                        value={applicationForm.companyName}
                        onChange={e => handleApplicationFieldChange('companyName', e.target.value)}
                        placeholder="Acme Telecom Partners"
                        disabled={applicationLoading || applicationSaving || applicationSubmitting}
                        className={appInputCls('companyName')}
                      />
                      <FieldError error={applicationFieldErrors.companyName} />
                    </div>

                    <div className="space-y-1.5">
                      <label htmlFor="app-company-type" className="text-xs font-medium text-foreground">
                        Company type <span className="text-destructive">*</span>
                      </label>
                      <SilkSelect
                        id="app-company-type"
                        value={applicationForm.companyType}
                        onValueChange={v => handleApplicationFieldChange('companyType', v)}
                        disabled={applicationLoading || applicationSaving || applicationSubmitting}
                        className={cn(
                          'h-9 w-full text-sm',
                          applicationFieldErrors.companyType &&
                            'border-destructive ring-1 ring-destructive/30',
                        )}
                      >
                        <option value="" disabled>Select type…</option>
                        {COMPANY_TYPE_OPTIONS.map(o => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </SilkSelect>
                      <FieldError error={applicationFieldErrors.companyType} />
                    </div>

                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="space-y-1.5">
                        <label htmlFor="app-employee-count" className="text-xs font-medium text-foreground">
                          Team size <span className="text-destructive">*</span>
                        </label>
                        <SilkSelect
                          id="app-employee-count"
                          value={applicationForm.employeeCountBand}
                          onValueChange={v => handleApplicationFieldChange('employeeCountBand', v)}
                          disabled={applicationLoading || applicationSaving || applicationSubmitting}
                          className={cn(
                            'h-9 w-full text-sm',
                            applicationFieldErrors.employeeCountBand &&
                              'border-destructive ring-1 ring-destructive/30',
                          )}
                        >
                          <option value="" disabled>Select range…</option>
                          {EMPLOYEE_COUNT_OPTIONS.map(o => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </SilkSelect>
                        <FieldError error={applicationFieldErrors.employeeCountBand} />
                      </div>

                      <div className="space-y-1.5">
                        <label htmlFor="app-website" className="text-xs font-medium text-foreground">
                          Website{' '}
                          <span className="text-[10px] font-normal text-muted-foreground/60">(optional)</span>
                        </label>
                        <SilkInput
                          id="app-website"
                          type="url"
                          value={applicationForm.website}
                          onChange={e => handleApplicationFieldChange('website', e.target.value)}
                          placeholder="https://example.com"
                          disabled={applicationLoading || applicationSaving || applicationSubmitting}
                          className="h-9 text-sm"
                        />
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      <label htmlFor="app-country-code" className="text-xs font-medium text-foreground">
                        Country <span className="text-destructive">*</span>
                      </label>
                      <SilkSelect
                        id="app-country-code"
                        value={applicationForm.countryCode}
                        onValueChange={v => handleApplicationFieldChange('countryCode', v)}
                        disabled={applicationLoading || applicationSaving || applicationSubmitting}
                        className="h-9 w-full text-sm"
                      >
                        {COUNTRY_OPTIONS.map(o => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </SilkSelect>
                    </div>
                  </div>

                  <div className="flex items-center justify-end pt-2">
                    <button
                      type="button"
                      onClick={() => handleApplicationTabChange('details')}
                      className="flex items-center gap-1.5 rounded-lg bg-primary px-5 py-2.5 text-sm font-bold text-primary-foreground shadow-sm transition-all hover:opacity-90 active:scale-[0.99]"
                    >
                      Next: Your details
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )}

              {/* ── Step 2: details ───────────────────────────────────── */}
              {applicationWizardStep === 'details' && (
                <div className="space-y-7">
                  <div className="space-y-1">
                    <p className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/60">
                      Step 2 of 3
                    </p>
                    <h2 className="text-lg font-black">Your details</h2>
                    <p className="text-sm text-muted-foreground">
                      Who should we reach about this application? How do you plan to use the platform?
                    </p>
                  </div>

                  {applicationError && <Callout variant="error">{applicationError}</Callout>}

                  <div className="space-y-5">
                    <div className="space-y-1.5">
                      <label htmlFor="app-contact-name" className="text-xs font-medium text-foreground">
                        Full name <span className="text-destructive">*</span>
                      </label>
                      <SilkInput
                        id="app-contact-name"
                        value={applicationForm.contactName}
                        onChange={e => handleApplicationFieldChange('contactName', e.target.value)}
                        placeholder="Avery Customer"
                        disabled={applicationLoading || applicationSaving || applicationSubmitting}
                        className={appInputCls('contactName')}
                      />
                      <FieldError error={applicationFieldErrors.contactName} />
                    </div>

                    <div className="space-y-1.5">
                      <label htmlFor="app-contact-email" className="text-xs font-medium text-foreground">
                        Work email <span className="text-destructive">*</span>
                      </label>
                      <SilkInput
                        id="app-contact-email"
                        type="email"
                        value={applicationForm.contactEmail}
                        onChange={e => handleApplicationFieldChange('contactEmail', e.target.value)}
                        placeholder="ops@example.com"
                        disabled={applicationLoading || applicationSaving || applicationSubmitting}
                        className={appInputCls('contactEmail')}
                      />
                      <FieldError error={applicationFieldErrors.contactEmail} />
                    </div>

                    <Callout variant="info">
                      Signed in as <strong className="font-semibold">{emailLabel}</strong>. Contact
                      details are used for application correspondence.
                    </Callout>

                    <div className="space-y-1.5">
                      <label htmlFor="app-contact-phone" className="text-xs font-medium text-foreground">
                        Business phone{' '}
                        <span className="text-[10px] font-normal text-muted-foreground/60">(optional)</span>
                      </label>
                      <SilkInput
                        id="app-contact-phone"
                        type="tel"
                        value={applicationForm.contactPhone}
                        onChange={e => handleApplicationFieldChange('contactPhone', e.target.value)}
                        placeholder="+1 555 555 5555"
                        disabled={applicationLoading || applicationSaving || applicationSubmitting}
                        className="h-9 text-sm"
                      />
                    </div>

                    <div className="space-y-1.5">
                      <label htmlFor="app-intended-use" className="text-xs font-medium text-foreground">
                        How you'll use the platform{' '}
                        <span className="text-[10px] font-normal text-muted-foreground/60">(optional)</span>
                      </label>
                      <textarea
                        id="app-intended-use"
                        value={applicationForm.intendedUse}
                        onChange={e => handleApplicationFieldChange('intendedUse', e.target.value)}
                        placeholder="Reselling telecom services, viewing shared map layers, managing field operations…"
                        rows={4}
                        disabled={applicationLoading || applicationSaving || applicationSubmitting}
                        className={cn(
                          'w-full resize-y rounded border border-border/50 bg-background px-3 py-2.5 text-sm text-foreground',
                          'placeholder:text-muted-foreground/50 transition-colors',
                          'hover:border-border focus:outline-none focus:ring-1 focus:ring-ring disabled:opacity-50',
                        )}
                      />
                      <p className="text-[11px] text-muted-foreground/60">
                        The more context you provide, the faster our team can provision the right features.
                      </p>
                    </div>

                    <div className="space-y-1.5">
                      <label htmlFor="app-notes" className="text-xs font-medium text-foreground">
                        Additional notes{' '}
                        <span className="text-[10px] font-normal text-muted-foreground/60">(optional)</span>
                      </label>
                      <textarea
                        id="app-notes"
                        value={applicationForm.notes}
                        onChange={e => handleApplicationFieldChange('notes', e.target.value)}
                        placeholder="Anything the platform team should know while reviewing your company."
                        rows={3}
                        disabled={applicationLoading || applicationSaving || applicationSubmitting}
                        className={cn(
                          'w-full resize-y rounded border border-border/50 bg-background px-3 py-2.5 text-sm text-foreground',
                          'placeholder:text-muted-foreground/50 transition-colors',
                          'hover:border-border focus:outline-none focus:ring-1 focus:ring-ring disabled:opacity-50',
                        )}
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-2">
                    <button
                      type="button"
                      onClick={() => handleApplicationTabChange('info')}
                      className="flex items-center gap-1.5 rounded-lg px-4 py-2.5 text-sm font-bold text-muted-foreground transition-all hover:bg-muted/40 hover:text-foreground"
                    >
                      <ArrowLeft className="h-4 w-4" />
                      Back
                    </button>
                    <button
                      type="button"
                      onClick={() => handleApplicationTabChange('review')}
                      className="flex items-center gap-1.5 rounded-lg bg-primary px-5 py-2.5 text-sm font-bold text-primary-foreground shadow-sm transition-all hover:opacity-90 active:scale-[0.99]"
                    >
                      Review & submit
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )}

              {/* ── Step 3: review ────────────────────────────────────── */}
              {applicationWizardStep === 'review' && (
                <div className="space-y-7">
                  <div className="space-y-1">
                    <p className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/60">
                      Step 3 of 3
                    </p>
                    <h2 className="text-lg font-black">Review & submit</h2>
                    <p className="text-sm text-muted-foreground">
                      Check your details, then submit for platform review.
                    </p>
                  </div>

                  {applicationError && <Callout variant="error">{applicationError}</Callout>}
                  {applicationNotice && <Callout variant="success">{applicationNotice}</Callout>}
                  {rejectedApplication && (
                    <Callout variant="warning">
                      <strong className="font-semibold">Previous application was returned:</strong>{' '}
                      {application?.latestReviewReason?.trim() ||
                        'Update the details on the previous steps and resubmit.'}
                    </Callout>
                  )}

                  <div className="overflow-hidden rounded-xl border border-border/50 bg-muted/10">
                    <InspectorSection title="Company" className="border-b border-border/40 p-5">
                      <PropertyGrid cols={2}>
                        <PropertyItem label="Name" value={applicationForm.companyName.trim() || '—'} mono={false} />
                        <PropertyItem
                          label="Type"
                          value={COMPANY_TYPE_OPTIONS.find(o => o.value === applicationForm.companyType)?.label ?? '—'}
                          mono={false}
                        />
                        <PropertyItem
                          label="Team size"
                          value={EMPLOYEE_COUNT_OPTIONS.find(o => o.value === applicationForm.employeeCountBand)?.label ?? '—'}
                          mono={false}
                        />
                        <PropertyItem
                          label="Country"
                          value={COUNTRY_OPTIONS.find(o => o.value === applicationForm.countryCode)?.label ?? applicationForm.countryCode}
                          mono={false}
                        />
                        {applicationForm.website.trim() && (
                          <PropertyItem label="Website" value={applicationForm.website.trim()} mono={false} />
                        )}
                      </PropertyGrid>
                    </InspectorSection>

                    <InspectorSection title="Contact" className="border-b border-border/40 p-5">
                      <PropertyGrid cols={2}>
                        <PropertyItem label="Name" value={applicationForm.contactName.trim() || '—'} mono={false} />
                        <PropertyItem label="Email" value={applicationForm.contactEmail.trim() || '—'} mono={false} />
                        {applicationForm.contactPhone.trim() && (
                          <PropertyItem label="Phone" value={applicationForm.contactPhone.trim()} mono={false} />
                        )}
                      </PropertyGrid>
                    </InspectorSection>

                    {applicationForm.intendedUse.trim() || applicationForm.notes.trim() ? (
                      <InspectorSection title="Use case" className="p-5">
                        {applicationForm.intendedUse.trim() && (
                          <p className="whitespace-pre-line text-xs leading-relaxed text-foreground">
                            {applicationForm.intendedUse.trim()}
                          </p>
                        )}
                        {applicationForm.notes.trim() && (
                          <p className="mt-3 whitespace-pre-line text-xs leading-relaxed text-muted-foreground">
                            {applicationForm.notes.trim()}
                          </p>
                        )}
                      </InspectorSection>
                    ) : (
                      <div className="border-t border-border/40 px-5 py-3">
                        <p className="text-xs italic text-muted-foreground/50">No use case description provided.</p>
                      </div>
                    )}
                  </div>

                  {!applicationAllComplete && (
                    <Callout variant="warning">
                      Required fields are incomplete. Go back and fill in all fields marked{' '}
                      <strong className="font-semibold">*</strong>.
                    </Callout>
                  )}

                  <button
                    type="button"
                    disabled={applicationLoading || applicationSaving || applicationSubmitting || !applicationAllComplete}
                    onClick={() => void handleSubmitApplication()}
                    className={cn(
                      'flex w-full items-center justify-center gap-2 rounded-xl px-6 py-4',
                      'text-sm font-bold shadow-sm transition-all',
                      'bg-primary text-primary-foreground hover:opacity-90 active:scale-[0.99]',
                      'disabled:pointer-events-none disabled:opacity-50',
                    )}
                  >
                    {applicationSubmitting ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <CheckCircle2 className="h-4 w-4" />
                    )}
                    {applicationSubmitting
                      ? rejectedApplication ? 'Resubmitting…' : 'Submitting…'
                      : rejectedApplication ? 'Resubmit for review' : 'Submit for review'}
                  </button>

                  <p className="text-center text-[11px] text-muted-foreground/60">
                    After submission, the platform team reviews your company and provisions your tenant workspace.
                  </p>

                  <div className="flex pt-1">
                    <button
                      type="button"
                      onClick={() => handleApplicationTabChange('details')}
                      className="flex items-center gap-1.5 rounded-lg px-4 py-2.5 text-sm font-bold text-muted-foreground transition-all hover:bg-muted/40 hover:text-foreground"
                    >
                      <ArrowLeft className="h-4 w-4" />
                      Back to your details
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Right: context panel */}
            <aside className="space-y-5 lg:sticky lg:top-8">
              {autoSaveStatus !== 'idle' && (
                <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground/60">
                  {autoSaveStatus === 'saving' && (
                    <><Loader2 className="h-3 w-3 animate-spin" /> Saving draft…</>
                  )}
                  {autoSaveStatus === 'saved' && (
                    <><CheckCircle2 className="h-3 w-3 text-emerald-500" /> Draft saved</>
                  )}
                  {autoSaveStatus === 'error' && (
                    <><AlertCircle className="h-3 w-3 text-destructive" /> Could not save draft</>
                  )}
                </div>
              )}

              {rejectedApplication && application?.latestReviewReason && (
                <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-4">
                  <p className="text-xs font-semibold text-amber-700">Application returned</p>
                  <p className="mt-1 text-xs text-amber-700/80 leading-relaxed">
                    {application.latestReviewReason.trim()}
                  </p>
                </div>
              )}

              <div className="rounded-xl border border-border/50 bg-muted/10 p-5 space-y-4">
                <div>
                  <p className="text-xs font-bold text-foreground">What happens next</p>
                  <p className="mt-0.5 text-[11px] text-muted-foreground leading-relaxed">
                    After you submit, our team reviews your details and provisions your workspace.
                  </p>
                </div>
                <ol className="space-y-3">
                  {[
                    'Our team reviews your company details.',
                    'We provision your home tenant and owner membership.',
                    'The portal unlocks and routes you into setup.',
                  ].map((text, i) => (
                    <li key={i} className="flex items-start gap-2.5">
                      <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-muted text-[9px] font-black text-muted-foreground">
                        {i + 1}
                      </span>
                      <span className="text-[11px] text-muted-foreground leading-relaxed">{text}</span>
                    </li>
                  ))}
                </ol>
              </div>

              <p className="text-[11px] text-muted-foreground/50 leading-relaxed">
                Your progress is saved automatically. You can close this page and return any time to finish your application.
              </p>
            </aside>
          </div>
        </div>
      </div>
    );
  }

  // ── Render: pending approval ───────────────────────────────────────────────

  if (pendingTenantApproval) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-6 py-16 text-foreground">
        <div className="w-full max-w-3xl space-y-6">
          <div className="space-y-1">
            <SectionEyebrow>Company approval pending</SectionEyebrow>
            <h1 className="text-2xl font-bold text-foreground">
              {portalAccess?.title ?? 'Your application is under review'}
            </h1>
            <p className="text-sm text-muted-foreground">
              {portalAccess?.message ??
                'We have your details and are reviewing the tenant request now. You can close this page and come back later.'}
            </p>
          </div>

          <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,0.75fr)]">
            <div className="overflow-hidden rounded-xl border border-border/50 bg-muted/10">
              <InspectorSection title="Application summary" className="p-5">
                {applicationLoading ? (
                  <p className="text-xs text-muted-foreground animate-pulse">
                    Loading application…
                  </p>
                ) : (
                  <PropertyGrid cols={2}>
                    <PropertyItem
                      label="Company"
                      value={application?.companyName ?? 'On file'}
                      mono={false}
                    />
                    <PropertyItem
                      label="Team size"
                      value={application?.employeeCountBand ?? '—'}
                      mono={false}
                    />
                    <PropertyItem
                      label="Contact"
                      value={application?.contactName ?? session?.email ?? '—'}
                      mono={false}
                    />
                    <PropertyItem
                      label="Status"
                      value={
                        <RegistryStatus label={application?.status ?? 'submitted'} variant="warning" />
                      }
                      mono={false}
                    />
                  </PropertyGrid>
                )}
              </InspectorSection>
            </div>

            <div className="space-y-4 rounded-xl border border-border/50 bg-muted/10 p-5">
              <div>
                <h2 className="text-sm font-semibold text-foreground">What happens next</h2>
                <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
                  The portal will unlock automatically once provisioning is complete.
                </p>
              </div>
              <ol className="space-y-2 text-xs text-muted-foreground">
                <li className="flex items-start gap-2">
                  <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-muted text-[9px] font-black">1</span>
                  Platform review confirms your company details.
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-muted text-[9px] font-black">2</span>
                  We provision your home tenant and owner membership.
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-muted text-[9px] font-black">3</span>
                  The portal unlocks and routes you into setup.
                </li>
              </ol>
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() => void handleRefreshStatus()}
                disabled={checkingStatus}
              >
                <RefreshCw className={cn('mr-1.5 h-3.5 w-3.5', checkingStatus && 'animate-spin')} />
                {checkingStatus ? 'Checking…' : 'Check for updates'}
              </Button>
              <p className="text-[11px] text-muted-foreground/60">
                Auto-checking every 15 seconds.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Render: awaiting tenant access ────────────────────────────────────────

  if (awaitingTenantAccess || pendingTenantAccessApproval) {
    const pendingRequests = agentRequests.filter(r => r.status === 'requested');

    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-6 py-16 text-foreground">
        <div className="w-full max-w-3xl space-y-6">
          <div className="space-y-1">
            <SectionEyebrow>
              {pendingTenantAccessApproval ? 'Tenant approval pending' : 'Join a partner tenant'}
            </SectionEyebrow>
            <h1 className="text-2xl font-bold text-foreground">
              {portalAccess?.title ??
                (pendingTenantAccessApproval
                  ? 'Your access request is waiting for approval'
                  : 'Your account is ready — join a tenant to continue')}
            </h1>
            <p className="text-sm text-muted-foreground">
              {portalAccess?.message ??
                (pendingTenantAccessApproval
                  ? 'A tenant administrator still needs to approve your reseller access.'
                  : 'You do not belong to a tenant yet. Enter a tenant-issued code or open an invite link to request access.')}
            </p>
          </div>

          {(agentAccessError || redeemError) && (
            <Callout variant="error">{redeemError ?? agentAccessError}</Callout>
          )}

          <div className="grid gap-5 lg:grid-cols-[minmax(0,1.3fr)_minmax(0,0.7fr)]">
            {/* Pending requests */}
            <div className="space-y-3">
              <div>
                <h2 className="text-sm font-semibold text-foreground">Access requests</h2>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {pendingTenantAccessApproval
                    ? 'Waiting on tenant-owner approval.'
                    : 'Invite links redeem automatically when opened.'}
                </p>
              </div>

              {agentAccessLoading ? (
                <Callout variant="info">
                  <span className="inline-flex items-center gap-2">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Loading access requests…
                  </span>
                </Callout>
              ) : pendingRequests.length > 0 ? (
                <RegistryList>
                  {pendingRequests.map(request => (
                    <RegistryRow
                      key={request.id}
                      avatar={<RegistryAvatar name={formatPendingRequestLabel(request)} />}
                      title={formatPendingRequestLabel(request)}
                      subtitle={`Requested ${new Date(request.requestedAt).toLocaleString()}`}
                      actions={<RegistryStatus label="Pending" variant="warning" />}
                    />
                  ))}
                </RegistryList>
              ) : (
                <div className="rounded-xl border border-dashed border-border/50 bg-muted/10 p-6 text-center">
                  <p className="text-sm text-muted-foreground">No tenant requests on file.</p>
                </div>
              )}
            </div>

            {/* Redeem / check status */}
            <div className="space-y-4 rounded-xl border border-border/50 bg-muted/10 p-5">
              <div>
                <h2 className="text-sm font-semibold text-foreground">
                  {pendingTenantAccessApproval ? 'Check status' : 'Redeem a code'}
                </h2>
                <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
                  {pendingTenantAccessApproval
                    ? 'Refresh after a tenant owner approves your request.'
                    : 'Enter a tenant-issued code to request access.'}
                </p>
              </div>

              {!pendingTenantAccessApproval && (
                <div className="space-y-2">
                  <SilkInput
                    value={redeemCode}
                    onChange={e => setRedeemCode(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') void handleRedeem(redeemCode);
                    }}
                    placeholder="Tenant invite code"
                    className="h-9 text-sm uppercase"
                  />
                  <Button
                    className="w-full"
                    size="sm"
                    disabled={redeeming || !redeemCode.trim()}
                    onClick={() => void handleRedeem(redeemCode)}
                  >
                    {redeeming ? 'Redeeming…' : 'Request access'}
                  </Button>
                </div>
              )}

              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() => void handleRefreshStatus()}
                disabled={checkingStatus}
              >
                <RefreshCw className={cn('mr-1.5 h-3.5 w-3.5', checkingStatus && 'animate-spin')} />
                {checkingStatus ? 'Checking…' : 'Refresh status'}
              </Button>
              <p className="text-[11px] text-muted-foreground/60">
                Auto-checking every 15 seconds.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Render: verification / blocked / provisioning ─────────────────────────

  const eyebrow = blocked
    ? 'Access review required'
    : awaitingVerification
      ? 'Email verification required'
      : 'Provisioning in progress';

  const title =
    portalAccess?.title ??
    (blocked
      ? 'Portal access needs support attention'
      : awaitingVerification
        ? 'Verify your email to continue'
        : 'Checking your portal access');

  const description =
    portalAccess?.message ??
    (awaitingVerification
      ? `Check ${emailLabel} for a verification link to complete your account setup.`
      : 'We are checking your account status and will unlock the next step as soon as it is ready.');

  const steps = blocked
    ? [
        'Your account has signed in successfully.',
        'A backend access dependency still needs operator or support attention.',
        'Use the refresh action after access has been updated, or contact support if this persists.',
      ]
    : awaitingVerification
      ? [
          `Open the verification email sent to ${emailLabel} and click the confirmation link.`,
          'Return to the portal once the link has been confirmed.',
          'Select "I have verified my email" to refresh your status and continue.',
        ]
      : [
          'We are confirming your current onboarding state.',
          'If your company application is ready, we will reopen it here.',
          'If your tenant is active, we will unlock the portal automatically.',
        ];

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-6 py-16 text-foreground">
      <div className="w-full max-w-lg space-y-6">
        <div className="space-y-1">
          <SectionEyebrow>{eyebrow}</SectionEyebrow>
          <h1 className="text-2xl font-bold text-foreground">{title}</h1>
          <p className="text-sm text-muted-foreground">{description}</p>
        </div>

        {blocked && (
          <Callout variant="warning">
            <span className="inline-flex items-start gap-2">
              <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                <strong className="font-semibold">Support action may be required.</strong> The
                portal can see your authenticated session but your account access is not fully
                configured yet.
              </span>
            </span>
          </Callout>
        )}

        <div className="rounded-xl border border-border/50 bg-muted/10 p-5">
          <ol className="space-y-3">
            {steps.map((step, index) => (
              <li key={`provision-step-${index}`} className="flex items-start gap-3 text-sm text-muted-foreground">
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-border/50 bg-muted text-[10px] font-black">
                  {index + 1}
                </span>
                {step}
              </li>
            ))}
          </ol>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Button onClick={() => void handleRefreshStatus()} disabled={checkingStatus}>
              <RefreshCw className={cn('mr-1.5 h-3.5 w-3.5', checkingStatus && 'animate-spin')} />
              {checkingStatus
                ? 'Checking…'
                : awaitingVerification
                  ? 'I have verified my email'
                  : blocked
                    ? 'Check again'
                    : 'Refresh status'}
            </Button>
            <span className="text-xs text-muted-foreground">
              {blocked
                ? 'Refresh after support updates your access.'
                : 'Auto-checking every 15 seconds.'}
            </span>
          </div>
        </div>

        <p className="text-xs text-muted-foreground">
          {blocked
            ? 'If this screen persists, contact support and mention that portal access is blocked for your signed-in account.'
            : 'Need help? Check your spam folder or reach out to support if the verification email has not arrived.'}
        </p>
      </div>
    </div>
  );
}
